/**
 * Salon des Rêves Page Module
 * Contains page-specific functionality for salondesreves.html
 * 
 * @module salondesrevesPage
 * @requires gsap - GSAP animation library
 * @requires gsap/ScrollTrigger - GSAP ScrollTrigger plugin
 * 
 * @example
 * // Import and initialize:
 * import { initSalonDesReves } from './salondesreves-page.js';
 * document.addEventListener('DOMContentLoaded', initSalonDesReves);
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Module state
let carousels = [];
let galleries = [];
let scrollTriggers = [];
let flipCards = [];

/**
 * Image Carousel Class
 * Handles carousel functionality for the Salon des Rêves page
 */
class ImageCarousel {
    constructor(container) {
        this.container = container;
        this.track = container.querySelector('.carousel-track');
        this.slides = container.querySelectorAll('.carousel-slide');
        this.indicators = container.querySelectorAll('.indicator');
        this.prevBtn = container.querySelector('.carousel-nav.carousel-prev');
        this.nextBtn = container.querySelector('.carousel-nav.carousel-next');
        this.currentIndex = 0;
        this.totalSlides = this.slides.length;
        this.hasPlayedSound = false;
        this.currentSoundSource = null;
        this.observer = null;

        this.init();
    }

    // ... [Previous ImageCarousel class methods remain the same] ...
    init() {
        // Set up event listeners
        this.prevBtn.addEventListener('click', () => {
            this.playInteractionSound();
            this.prevSlide();
        });
        this.nextBtn.addEventListener('click', () => {
            this.playInteractionSound();
            this.nextSlide();
        });

        // Set up indicator clicks
        this.indicators.forEach((indicator, index) => {
            indicator.addEventListener('click', () => {
                this.playInteractionSound();
                this.goToSlide(index);
            });
        });

        // Show first slide
        this.updateSlides();

        // Set up viewport observer
        this.setupViewportObserver();
    }

    playInteractionSound() {
        if (this.hasPlayedSound || !audioManager) {
            return;
        }

        if (audioManager.sounds && audioManager.sounds.has('poussiere-d-ange')) {
            this.stopAllSounds();
            this.currentSoundSource = audioManager.playSound('poussiere-d-ange', {
                volume: 1.0,
                playbackRate: 1.0
            });
            this.hasPlayedSound = true;
        }
    }

    setupViewportObserver() {
        const cardElement = this.container.closest('.card') || this.container.closest('[class*="card"]');

        if (!cardElement) {
            console.warn('🎠 Could not find parent card element for viewport observer');
            return;
        }

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.resetSoundState();
                } else {
                    this.stopCurrentSound();
                }
            });
        }, {
            threshold: 0.05,
            rootMargin: '200px 0px -50px 0px'
        });

        this.observer.observe(cardElement);
    }

    stopCurrentSound() {
        if (this.currentSoundSource && typeof this.currentSoundSource.stop === 'function') {
            try {
                this.currentSoundSource.stop();
            } catch (error) {
                console.warn('🎵 Error stopping sound:', error);
            }
            this.currentSoundSource = null;
        }
    }

    stopAllSounds() {
        this.stopCurrentSound();

        if (audioManager?.stopAllSounds) {
            audioManager.stopAllSounds();
        }

        if (window.stopAllSalonAudio) {
            window.stopAllSalonAudio();
        }

        if (window.crescendoInterval) {
            clearInterval(window.crescendoInterval);
            window.crescendoInterval = null;
        }
    }

    resetSoundState() {
        this.hasPlayedSound = false;
    }

    updateSlides() {
        this.slides.forEach((slide, index) => {
            slide.classList.toggle('active', index === this.currentIndex);
        });

        this.indicators.forEach((indicator, index) => {
            indicator.classList.toggle('active', index === this.currentIndex);
        });
    }

    nextSlide() {
        this.currentIndex = (this.currentIndex + 1) % this.totalSlides;
        this.updateSlides();
    }

    prevSlide() {
        this.currentIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
        this.updateSlides();
    }

    goToSlide(index) {
        if (index >= 0 && index < this.totalSlides) {
            this.currentIndex = index;
            this.updateSlides();
        }
    }

    destroy() {
        // Clean up event listeners
        this.prevBtn.removeEventListener('click', this.prevSlide);
        this.nextBtn.removeEventListener('click', this.nextSlide);
        
        this.indicators.forEach((indicator, index) => {
            indicator.removeEventListener('click', () => this.goToSlide(index));
        });

        // Clean up observer
        if (this.observer) {
            this.observer.disconnect();
        }

        // Stop any playing sounds
        this.stopAllSounds();
    }
}

/**
 * Interactive Gallery Class
 * Handles the interactive gallery functionality for the Salon des Rêves page
 */
class InteractiveGallery {
    constructor(container) {
        this.container = container;
        this.items = container.querySelectorAll('.gallery-item');
        this.currentExpanded = null;
        this.init();
    }

    init() {
        this.items.forEach((item, index) => {
            item.addEventListener('click', () => this.handleItemClick(item, index));
        });
    }

    handleItemClick(clickedItem) {
        const index = Array.from(this.items).indexOf(clickedItem);
        if (index === -1) return;

        if (this.currentExpanded === index) {
            this.resetGallery();
        } else {
            this.expandItem(index);
        }
    }

    expandItem(index) {
        // Collapse current expanded item if any
        if (this.currentExpanded !== null) {
            this.resetGallery();
        }

        // Expand new item
        this.currentExpanded = index;
        this.items[index].classList.add('expanded');
        
        // Add click outside to close
        setTimeout(() => {
            document.addEventListener('click', this.handleClickOutside);
        }, 10);
    }

    resetGallery() {
        if (this.currentExpanded !== null) {
            this.items[this.currentExpanded].classList.remove('expanded');
            this.currentExpanded = null;
            document.removeEventListener('click', this.handleClickOutside);
        }
    }

    handleClickOutside = (e) => {
        if (!this.container.contains(e.target)) {
            this.resetGallery();
        }
    }

    destroy() {
        this.items.forEach(item => {
            item.removeEventListener('click', this.handleItemClick);
        });
        document.removeEventListener('click', this.handleClickOutside);
    }
}

/**
 * Initialize all Salon des Rêves page functionality
 * @public
 * @returns {Function} Cleanup function to remove event listeners and clean up resources
 */
export function initSalonDesReves() {
    // Initialize carousels
    const carouselElements = document.querySelectorAll('.carousel-container');
    carouselElements.forEach(element => {
        carousels.push(new ImageCarousel(element));
    });

    // Initialize galleries
    const galleryElements = document.querySelectorAll('.gallery-container');
    galleryElements.forEach(element => {
        galleries.push(new InteractiveGallery(element));
    });

    // Initialize flip cards
    initFlipCards();

    // Set up work showcase animations
    setupWorkShowcase();

    // Cleanup function
    return () => {
        // Clean up carousels
        carousels.forEach(carousel => carousel.destroy());
        carousels = [];

        // Clean up galleries
        galleries.forEach(gallery => gallery.destroy());
        galleries = [];

        // Clean up flip cards
        cleanupFlipCards();

        // Clean up ScrollTriggers
        scrollTriggers.forEach(trigger => {
            if (trigger && typeof trigger.kill === 'function') {
                trigger.kill();
            }
        });
        scrollTriggers = [];
    };
}

/**
 * Initialize flip cards functionality
 */
function initFlipCards() {
    const flipCardElements = document.querySelectorAll('.joke-flip-card');
    
    flipCardElements.forEach(card => {
        const handleClick = () => {
            // Toggle the flipped class
            card.classList.toggle('flipped');

            // Add a subtle animation effect
            const img = card.querySelector('img');
            if (img) {
                img.style.transform = card.classList.contains('flipped') ? 'scale(1.1)' : 'scale(1)';
                setTimeout(() => {
                    img.style.transform = '';
                }, 400);
            }
        };

        card.addEventListener('click', handleClick);
        flipCards.push({ element: card, handler: handleClick });
    });
}

/**
 * Cleanup flip cards event listeners
 */
function cleanupFlipCards() {
    flipCards.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
    });
    flipCards = [];
}

/**
 * Set up work showcase animations using GSAP and ScrollTrigger
 */
function setupWorkShowcase() {
    // Set initial states for animation
    gsap.set(".work-item", {
        y: 100,
        rotation: 0, // Will be overridden per item
        opacity: 0
    });

    // Apply initial rotation to items
    document.querySelectorAll(".work-row").forEach((row) => {
        const workItems = row.querySelectorAll(".work-item");

        workItems.forEach((item, itemIndex) => {
            const isLeftItem = itemIndex === 0;
            gsap.set(item, {
                rotation: isLeftItem ? -15 : 15,
                transformOrigin: "center center"
            });
        });

        // Create ScrollTrigger for this row
        const trigger = ScrollTrigger.create({
            trigger: row,
            start: "top 75%", // Trigger when top of row is 75% from top of viewport
            onEnter: () => {
                const workItems = row.querySelectorAll(".work-item");

                // Animate items with stagger
                gsap.to(workItems, {
                    y: 0,
                    rotation: 0,
                    opacity: 1,
                    duration: 1,
                    ease: "power4.out",
                    stagger: 0.2, // Stagger between items in the same row
                    onStart: () => {
                        // Add animate-in class for CSS transitions as backup
                        workItems.forEach(item => item.classList.add('animate-in'));
                    }
                });
            },
            once: true // Only trigger once
        });

        // Store ScrollTrigger instance for cleanup
        scrollTriggers.push(trigger);
    });
}

/**
 * Debug function for gallery
 * @private
 */
function debugGallery() {
    const galleryContainers = document.querySelectorAll('.gallery-container');
    console.log('=== Gallery Debug Info ===');
    console.log(`Found ${galleryContainers.length} gallery containers`);
    
    galleryContainers.forEach((gallery, index) => {
        const items = gallery.querySelectorAll('.gallery-item');
        const expanded = gallery.querySelector('.gallery-item.expanded');
        
        console.log(`\nGallery #${index + 1}:`);
        console.log(`- Total items: ${items.length}`);
        console.log(`- Currently expanded: ${expanded ? 'Yes' : 'No'}`);
        
        items.forEach((item, i) => {
            console.log(`  Item ${i + 1}:`, {
                hasImage: !!item.querySelector('img'),
                isExpanded: item.classList.contains('expanded')
            });
        });
    });
}

/**
 * Debug function for carousel
 * @private
 */
function debugCarousel() {
    const carouselContainers = document.querySelectorAll('.carousel-container');
    console.log('=== Carousel Debug Info ===');
    console.log(`Found ${carouselContainers.length} carousel containers`);
    
    carouselContainers.forEach((carousel, index) => {
        const track = carousel.querySelector('.carousel-track');
        const slides = carousel.querySelectorAll('.carousel-slide');
        const prevBtn = carousel.querySelector('.carousel-nav.carousel-prev');
        const nextBtn = carousel.querySelector('.carousel-nav.carousel-next');
        const indicators = carousel.querySelectorAll('.indicator');
        const activeSlide = carousel.querySelector('.carousel-slide.active');
        
        console.log(`\nCarousel #${index + 1}:`);
        console.log(`- Total slides: ${slides.length}`);
        console.log(`- Active slide: ${activeSlide ? 'Found' : 'None'}`);
        console.log(`- Navigation: ${prevBtn && nextBtn ? 'Found' : 'Missing'}`);
        console.log(`- Indicators: ${indicators.length} found`);
        
        // Check for required elements
        if (!track) console.warn('  - Missing: .carousel-track');
        if (slides.length === 0) console.warn('  - No slides found');
        if (!prevBtn) console.warn('  - Missing: Previous button');
        if (!nextBtn) console.warn('  - Missing: Next button');
        
        // Check if inside card-3
        const inCard3 = carousel.closest('#card-3');
        console.log(`  - Inside #card-3: ${inCard3 ? 'Yes' : 'No'}`);
        
        // Check if any slides are visible
        const visibleSlides = Array.from(slides).filter(slide => {
            return slide.offsetParent !== null;
        });
        console.log(`  - Visible slides: ${visibleSlides.length}/${slides.length}`);
    });
}

// Default export for backward compatibility
export default { init: initSalonDesReves };

// Auto-initialize when DOM is ready (matches original behavior)
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSalonDesReves);
} else {
    // DOM already loaded
    initSalonDesReves();
}
